import {ChevronRight} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Badge} from '@common-web/components/ui/badge';
import {ActiveFilterPills, ConsolidatedFilterBar} from '@internal/pages/claims/ConsolidatedFilterBar';
import {useConsolidatedFilters} from '@internal/pages/claims/useConsolidatedFilters';
import type {StatusCount} from '@api/lib/claimsTracking/types';

export function OverviewTab({
  onDrillToExCodes,
  onDrillToGroup,
  onDrillToExCode,
}: {
  onDrillToExCodes: () => void;
  onDrillToGroup: (groupId: string) => void;
  onDrillToExCode: (exCode: string) => void;
}) {
  const {filters} = useConsolidatedFilters();

  const {data: summary, isLoading} =
    trpc.internal.claimsTracking.consolidated.getOverview.useQuery(filters);
  const {data: topGroups, isLoading: groupsLoading} =
    trpc.internal.claimsTracking.consolidated.getTopGroups.useQuery({...filters, limit: 10});
  const {data: topExCodes, isLoading: exLoading} =
    trpc.internal.claimsTracking.consolidated.getTopExCodes.useQuery({...filters, limit: 10});

  return (
    <div className='space-y-6'>
      <Card>
        <CardContent className='pt-6 space-y-3'>
          <div className='flex items-end gap-3 flex-wrap'>
            <ConsolidatedFilterBar />
          </div>
          <ActiveFilterPills />
        </CardContent>
      </Card>

      <StatusBreakdown
        statusCounts={summary?.statusCounts}
        total={summary?.total}
        isLoading={isLoading}
      />

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <ListCard
          title='Top groups'
          isLoading={groupsLoading}
          rows={topGroups?.map((g) => ({
            key: g.groupId ?? g.groupName ?? 'no-group',
            label: g.groupName ?? g.groupId ?? '(No Group ID)',
            count: g.claimCount,
            onClick: g.groupId ? () => onDrillToGroup(g.groupId as string) : undefined,
          }))}
        />
        <ListCard
          title='Top ex codes'
          actionLabel='View all'
          onAction={onDrillToExCodes}
          isLoading={exLoading}
          rows={topExCodes?.map((x) => ({
            key: `${x.level}-${x.exCode}`,
            label: x.description ?? x.exCode,
            badge: x.exCode,
            badgeTone: x.level === 'LINE' ? ('line' as const) : ('header' as const),
            count: x.claimCount,
            onClick: () => onDrillToExCode(x.exCode),
          }))}
        />
      </div>
    </div>
  );
}

function StatusBreakdown({
  statusCounts,
  total,
  isLoading,
}: {
  statusCounts: StatusCount[] | undefined;
  total: number | undefined;
  isLoading: boolean;
}) {
  const sorted = [...(statusCounts ?? [])].sort((a, b) => b.count - a.count);
  const max = Math.max(...sorted.map((s) => s.count), 1);
  const grandTotal = total ?? 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-base'>Claims by status</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        {isLoading
          ? Array.from({length: 6}).map((_, i) => <Skeleton key={i} className='h-8' />)
          : sorted.map((s) => {
              const width = `${Math.max((s.count / max) * 100, s.count > 0 ? 4 : 0)}%`;
              const pct = ((s.count / grandTotal) * 100).toFixed(1);
              const label = s.statusDescription
                ? `${s.status} — ${s.statusDescription}`
                : (s.status ?? '(unknown)');
              return (
                <div key={s.status ?? 'unknown'} className='flex items-center gap-3 text-sm'>
                  <span className='w-52 shrink-0 text-muted-foreground truncate' title={label}>
                    {label}
                  </span>
                  <div className='flex-1 h-7 bg-muted rounded-md overflow-hidden'>
                    <div className='h-full rounded-md bg-primary/70 transition-all' style={{width}} />
                  </div>
                  <span className='font-mono w-16 text-right tabular-nums'>
                    {s.count.toLocaleString()}
                  </span>
                  <span className='font-mono w-14 text-right tabular-nums text-muted-foreground text-xs'>
                    {pct}%
                  </span>
                </div>
              );
            })}
      </CardContent>
    </Card>
  );
}

type ListRow = {
  key: string;
  label: string;
  count: number;
  badge?: string;
  badgeTone?: 'header' | 'line';
  onClick?: () => void;
};

function ListCard({
  title,
  rows,
  isLoading,
  actionLabel,
  onAction,
}: {
  title: string;
  rows: ListRow[] | undefined;
  isLoading: boolean;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const toneClass: Record<NonNullable<ListRow['badgeTone']>, string> = {
    header: 'bg-orange-50 text-orange-700 border-orange-200',
    line: 'bg-violet-50 text-violet-700 border-violet-200',
  };

  return (
    <Card>
      <CardHeader className='flex-row items-center justify-between space-y-0'>
        <CardTitle className='text-base'>{title}</CardTitle>
        {actionLabel && onAction ? (
          <button
            type='button'
            onClick={onAction}
            className='text-xs text-primary hover:underline flex items-center gap-0.5'
          >
            {actionLabel}
            <ChevronRight className='h-3 w-3' />
          </button>
        ) : null}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='space-y-2'>
            {Array.from({length: 6}).map((_, i) => (
              <Skeleton key={i} className='h-6' />
            ))}
          </div>
        ) : rows && rows.length > 0 ? (
          <ul className='space-y-2'>
            {rows.map((row) => (
              <li key={row.key}>
                <button
                  type='button'
                  onClick={row.onClick}
                  disabled={!row.onClick}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 text-sm text-left rounded px-1 py-0.5',
                    row.onClick ? 'hover:bg-muted/60 cursor-pointer' : 'cursor-default',
                  )}
                >
                  <span className='flex items-center gap-2 min-w-0'>
                    {row.badge ? (
                      <Badge
                        variant='outline'
                        className={cn('font-mono shrink-0', row.badgeTone && toneClass[row.badgeTone])}
                      >
                        {row.badge}
                      </Badge>
                    ) : null}
                    <span className='truncate text-brand-navy' title={row.label}>
                      {row.label}
                    </span>
                  </span>
                  <span className='font-mono tabular-nums text-muted-foreground shrink-0'>
                    {row.count.toLocaleString()}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-sm text-muted-foreground'>No data.</p>
        )}
      </CardContent>
    </Card>
  );
}
